import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  RequireUUIDParams as RequireUUIDParamsBase,
  RequireUUIDParamsProps,
} from "../components";
import { useAdvancedCustomTemplatesEnabled } from "../hooks/useAdvancedCustomTemplatesEnabled";
import { RequireAuth, RequireCanAccessPrivateCalls } from "./components";
import AiInterviewerDetailView from "./components/AiInterviewer/AiInterviewerDetailView";
import { AiInterviewerWizardProviderWrapper } from "./components/AiInterviewerWizard/AiInterviewerWizardContext";
import {
  AdditionalInputsView,
  AiInterviewerCreateViewFork,
} from "./components/AiInterviewerWizard/FlexIntake";
import FeatureAccess from "./components/FeatureAccess/FeatureAccess";
import FeatureGate from "./components/FeatureGate";
import { FeatureName } from "./graphql";
import AiInterviewerPage from "./pages/ai-interviewer/AiInterviewerPage";
import { AiInterviewerUpgradePage } from "./pages/ai-interviewer/AiInterviewerUpgradePage";
import AiInterviewersPage from "./pages/ai-interviewers/AiInterviewersPage";
import AnalyticsMetrics from "./pages/analytics/AnalyticsMetrics";
import AnalyticsPage from "./pages/analytics/AnalyticsPage";
import AnalyticsReport from "./pages/analytics/AnalyticsReport";
import AnalyticsCandidateMotivations from "./pages/analytics/candidate-motivations/AnalyticsCandidateMotivations";
import AnalyticsCandidateQuestions from "./pages/analytics/candidate-questions/AnalyticsCandidateQuestions";
import AnalyticsCompensationTrends from "./pages/analytics/compensation-trends/AnalyticsCompensationTrends";
import CustomInsightsChatPage from "./pages/analytics/custom/CustomInsightsChatPage";
import CustomInsightsPageLayout from "./pages/analytics/custom/CustomInsightsPageLayout";
import CustomInsightsViewPage from "./pages/analytics/custom/CustomInsightsReport";
import AnalyticsMyAnalytics from "./pages/analytics/my-analytics/AnalyticsMyAnalytics";
import AnalyticsOverviewVersionSwitch from "./pages/analytics/overview/AnalyticsOverviewVersionSwitch";
import AnalyticsSkillsReport from "./pages/analytics/skills-report/AnalyticsSkillsReport";
import AnalyticsSkillsReports from "./pages/analytics/skills-report/AnalyticsSkillsReports";
import AnalyticsSkillsReportBuilder from "./pages/analytics/skills-report/builder/AnalyticsSkillsReportBuilder";
import AnalyticsTopicTrends from "./pages/analytics/topic-trends/AnalyticsTopicTrends";
import { SelectScorecardAutoDraftPage } from "./pages/auto-draft-scorecard";
import CancelRecordingPage from "./pages/cancel-recording/CancelRecordingPage";
import CancelRecordingByMeetingPage from "./pages/cancel-recording-by-meeting/CancelRecordingByMeetingPage";
import CandidatePage from "./pages/candidate/CandidatePage";
import CandidateEmailOptOutPage from "./pages/candidate-opt-out/CandidateEmailOptOutPage";
import VerifyEmailPage from "./pages/candidate-opt-out/VerifyEmailPage";
import CandidateSummaryEditPage from "./pages/candidate-summary/CandidateSummaryEditPage";
import CandidatesPage from "./pages/candidates/Candidates";
import ClipPage from "./pages/clip/ClipPage";
import MyClipsPage from "./pages/clips/MyClipsPage";
import ExtensionLandingPage from "./pages/extension/ExtensionLandingPage";
import ExternalSharePage from "./pages/external-share/ExternalSharePage";
import Forbidden from "./pages/forbidden/Forbidden";
import GuidePage from "./pages/guide/GuidePage";
import GuidesPage from "./pages/guides/GuidesPage";
import HomePage from "./pages/home/HomePage";
import { InstallZoomApp } from "./pages/install-zoom-app";
import InterviewPage from "./pages/interview/InterviewPage";
import InterviewOptInPage from "./pages/interview-opt-in/InterviewOptInPage";
import InterviewerOptOutPage from "./pages/interviewer-opt-out/InterviewerOptOutPage";
import InterviewsPage from "./pages/interviews/InterviewsPage";
import NoAccessPage from "./pages/no-access/NoAccessPage";
import NotFound from "./pages/not-found/NotFoundPage";
import MyPlaylistsPage from "./pages/playlists/MyPlaylistsPage";
import PositionPage from "./pages/position/PositionPage";
import PositionsPage from "./pages/positions/PositionsPage";
import RegisterInfoPage from "./pages/register-info/RegisterInfoPage";
import VerifyPhonePage from "./pages/register-phone/RegisterPhonePage";
import SearchPage from "./pages/search/SearchPage";
import SelectPersonaPage from "./pages/select-persona/SelectPersonaPage";
import DataRedactionLog from "./pages/settings/ComplianceSettings/DataRedactionLog";
import SettingsPage from "./pages/settings/SettingsPage";
import SignInPage from "./pages/sign-in/SignInPage";
import SignUpPage from "./pages/sign-up/SignUpPage";
import SupportPage from "./pages/support/SupportPage";
import SyncCalendarPage from "./pages/sync-calendar/SyncCalendarPage";
import TemplatePage from "./pages/template/TemplatePage";
import TemplatesPage from "./pages/templates/TemplatesPage";
import AllTraineesPage from "./pages/training/AllTraineesPage";
import ManageTrainingPage from "./pages/training/ManageTrainingPage";
import ManageTrainingRedirectPage from "./pages/training/ManageTrainingRedirectPage";
import ViewTrainingPage from "./pages/training/ViewTrainingPage";
import ViewTrainingRedirectPage from "./pages/training/ViewTrainingRedirectPage";
import UpgradePage from "./pages/upgrade/UpgradePage";

const RequireUUIDParams: React.FC<Omit<RequireUUIDParamsProps, "element">> = (
  props
) => <RequireUUIDParamsBase element={<NotFound />} {...props} />;

export const AppRoutes = (): JSX.Element => {
  return (
    <Routes>
      {["/", "/schedule"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <HomePage />
            </RequireAuth>
          }
        />
      ))}
      {[
        `/interview/:callId`,
        `/interview/:callId/add-to-training-program`,
        `/interview/:callId/share`,
        `/interview/:callId/edit`,
        `/interview/:callId/set-language`,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireUUIDParams paramNames={["callId"]}>
                <InterviewPage />
              </RequireUUIDParams>
            </RequireAuth>
          }
        />
      ))}
      {[
        `/interview/:callId/clip/:clipId`,
        `/interview/:callId/clip/:clipId/share`,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth hideIntercom>
              <RequireUUIDParams paramNames={["callId", "clipId"]}>
                <ClipPage />
              </RequireUUIDParams>
            </RequireAuth>
          }
        />
      ))}
      <Route
        path="/clips"
        element={
          <RequireAuth>
            <MyClipsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/interviews"
        element={
          <RequireAuth>
            <RequireCanAccessPrivateCalls redirectTo="/">
              <InterviewsPage />
            </RequireCanAccessPrivateCalls>
          </RequireAuth>
        }
      />
      <Route
        path="/playlists"
        element={
          <RequireAuth>
            <MyPlaylistsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/playlist/:playlistId"
        element={
          <RequireAuth>
            <RequireUUIDParams paramNames={["playlistId"]}>
              <MyPlaylistsPage />
            </RequireUUIDParams>
          </RequireAuth>
        }
      />
      {["/guides", "/guides/shared", "/guides/new", "/guides/my-assigned"].map(
        (path) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireAuth>
                <GuidesPage />
              </RequireAuth>
            }
          />
        )
      )}
      <Route
        path="guides/assigned"
        element={
          <RequireAuth>
            <GuidesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/guide/:callGuideId"
        element={
          <RequireAuth>
            <RequireUUIDParams paramNames={["callGuideId"]}>
              <GuidePage />
            </RequireUUIDParams>
          </RequireAuth>
        }
      />
      {["/positions", "/positions/mine", "/positions/new"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <PositionsPage />
            </RequireAuth>
          }
        />
      ))}
      {[
        "/position/:positionId",
        "/position/:positionId/candidates",
        "/position/:positionId/guides",
        "/position/:positionId/prospects",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireUUIDParams paramNames={["positionId"]}>
                <PositionPage />
              </RequireUUIDParams>
            </RequireAuth>
          }
        />
      ))}
      <Route
        path="/candidates"
        element={
          <RequireAuth>
            <CandidatesPage />
          </RequireAuth>
        }
      />
      {[
        "/candidate/:candidateId",
        "/candidate/:candidateId/:positionId",
        "/candidate/:candidateId/position/none",
        "/candidate/:candidateId/position/:positionId",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireUUIDParams paramNames={["candidateId", "positionId"]}>
                <CandidatePage />
              </RequireUUIDParams>
            </RequireAuth>
          }
        />
      ))}
      {["/templates", "/templates/shared"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireAdvancedCustomTemplates>
                <TemplatesPage />
              </RequireAdvancedCustomTemplates>
            </RequireAuth>
          }
        />
      ))}
      {[
        "/template/:templateId/pick",
        "/template/:templateId/example",
        "/template/:templateId/edit",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireAdvancedCustomTemplates>
                <RequireUUIDParams paramNames={["templateId"]}>
                  <TemplatePage />
                </RequireUUIDParams>
              </RequireAdvancedCustomTemplates>
            </RequireAuth>
          }
        />
      ))}
      {[
        "/template/new/pick",
        "/template/new/example",
        "/template/new/edit",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <RequireAdvancedCustomTemplates>
                <TemplatePage />
              </RequireAdvancedCustomTemplates>
            </RequireAuth>
          }
        />
      ))}
      <Route
        path="/debrief/:templateId/edit"
        element={
          <RequireAuth>
            <RequireUUIDParams paramNames={["templateId"]}>
              <CandidateSummaryEditPage />
            </RequireUUIDParams>
          </RequireAuth>
        }
      />
      <Route
        path="/training/manage"
        element={
          <RequireAuth>
            <FeatureAccess
              feature={FeatureName.Training}
              redirectRoute="/training/upgrade"
            >
              <ManageTrainingRedirectPage />
            </FeatureAccess>
          </RequireAuth>
        }
      />
      <Route
        path="/training/manage/all-trainees"
        element={
          <RequireAuth>
            <FeatureAccess
              feature={FeatureName.Training}
              redirectRoute="/training/upgrade"
            >
              <AllTraineesPage />
            </FeatureAccess>
          </RequireAuth>
        }
      />
      <Route
        path="/training/view"
        element={
          <RequireAuth>
            <FeatureAccess
              feature={FeatureName.Training}
              redirectRoute="/training/upgrade"
            >
              <ViewTrainingRedirectPage />
            </FeatureAccess>
          </RequireAuth>
        }
      />
      <Route
        path="/training/manage/:trainingProgramId"
        element={
          <RequireAuth>
            <FeatureAccess
              feature={FeatureName.Training}
              redirectRoute="/training/upgrade"
            >
              <RequireUUIDParams paramNames={["trainingProgramId"]}>
                <ManageTrainingPage />
              </RequireUUIDParams>
            </FeatureAccess>
          </RequireAuth>
        }
      />
      <Route
        path="/training/view/:trainingProgramId"
        element={
          <RequireAuth>
            <FeatureAccess
              feature={FeatureName.Training}
              redirectRoute="/training/upgrade"
            >
              <RequireUUIDParams paramNames={["trainingProgramId"]}>
                <ViewTrainingPage />
              </RequireUUIDParams>
            </FeatureAccess>
          </RequireAuth>
        }
      />
      <Route
        path="/training/upgrade"
        element={
          <RequireAuth>
            <UpgradePage
              headerText="Enhance your interview skills today"
              bodyText="Create personalized training programs with best-practice interview clips, empowering your teams to learn from real-world examples and conduct consistent, high-quality interviews every time."
              buttonText="Upgrade now"
              upgradeImageSrc="/static/images/interview-upgrade.png"
            />
          </RequireAuth>
        }
      />
      <Route
        path="/ai-interviewers"
        element={
          <RequireAuth>
            <AiInterviewersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/ai-interviewer/upgrade"
        element={
          <RequireAuth>
            <AiInterviewerUpgradePage />
          </RequireAuth>
        }
      />
      <Route
        path="/ai-interviewer"
        element={
          <RequireAuth>
            <AiInterviewerWizardProviderWrapper>
              <AiInterviewerPage />
            </AiInterviewerWizardProviderWrapper>
          </RequireAuth>
        }
      >
        <Route path="new" element={<AiInterviewerCreateViewFork />} />
        <Route
          path=":aiInterviewerId/train"
          element={
            <RequireUUIDParams paramNames={["aiInterviewerId"]}>
              <AdditionalInputsView />
            </RequireUUIDParams>
          }
        />
        <Route
          path=":aiInterviewerId/*"
          element={
            <RequireUUIDParams paramNames={["aiInterviewerId"]}>
              <AiInterviewerDetailView />
            </RequireUUIDParams>
          }
        />
      </Route>
      <Route path="/analytics/:metric" element={<InsightsPageRedirect />} />
      <Route path="/analytics" element={<InsightsPageRedirect />} />
      <Route
        path="/insights/upgrade"
        element={
          <RequireAuth>
            <UpgradePage
              headerText="Optimize hiring with BrightHire Insights"
              bodyText="Unlock actionable insights from every interview, allowing you to identify patterns, enhance decision-making, and continuously refine your hiring process for optimal results. Effortlessly measure interview quality with a tailored score, customized to fit the unique needs of your organization."
              buttonText="Upgrade now"
              upgradeImageSrc="/static/images/insights-upgrade.png"
            />
          </RequireAuth>
        }
      />
      <Route
        path="/insights"
        element={
          <RequireAuth>
            <AnalyticsPage />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route
          path="custom"
          element={
            <FeatureGate flag="insights:advanced-chat">
              <CustomInsightsPageLayout />
            </FeatureGate>
          }
        >
          <Route index element={<CustomInsightsChatPage />} />
          <Route path="reports/:id" element={<CustomInsightsViewPage />} />
        </Route>
        <Route
          path="overview"
          element={
            <FeatureAccess
              feature={FeatureName.Insights}
              redirectRoute="/insights/upgrade"
            >
              <AnalyticsOverviewVersionSwitch />
            </FeatureAccess>
          }
        />
        <Route
          path="report"
          element={
            <FeatureGate flag="analytics:quality-report">
              <AnalyticsReport />
            </FeatureGate>
          }
        />
        <Route
          path="interviewer/:interviewerId"
          element={
            <RequireUUIDParams paramNames={["interviewerId"]}>
              <AnalyticsMyAnalytics />
            </RequireUUIDParams>
          }
        />
        <Route
          path="topic-trends"
          element={
            <FeatureGate flag="analytics:topic-trends">
              <AnalyticsTopicTrends />
            </FeatureGate>
          }
        />
        <Route
          path="compensation-trends"
          element={
            <FeatureGate flag="analytics:compensation-trends">
              <AnalyticsCompensationTrends />
            </FeatureGate>
          }
        />
        <Route
          path="candidate-motivations"
          element={
            <FeatureGate flag="analytics:candidate-motivations">
              <AnalyticsCandidateMotivations />
            </FeatureGate>
          }
        />
        <Route
          path="candidate-questions"
          element={
            <FeatureGate flag="analytics:candidate-questions">
              <AnalyticsCandidateQuestions />
            </FeatureGate>
          }
        />
        <Route
          path="skill-report"
          element={
            <FeatureGate flag="analytics:skills-report">
              <AnalyticsSkillsReportBuilder />
            </FeatureGate>
          }
        />
        <Route
          path="skill-report/all"
          element={
            <FeatureGate flag="analytics:skills-report">
              <AnalyticsSkillsReports />
            </FeatureGate>
          }
        />
        <Route
          path="skill-report/:reportId"
          element={
            <FeatureGate flag="analytics:skills-report">
              <AnalyticsSkillsReport />
            </FeatureGate>
          }
        />
        <Route path=":metric" element={<AnalyticsMetrics />} />
      </Route>
      <Route path="/users/*" element={<UsersPageRedirect />} />
      <Route
        path="/interviewer-opt-out/:interviewerId"
        element={
          <RequireAuth>
            <RequireUUIDParams paramNames={["interviewerId"]}>
              <InterviewerOptOutPage />
            </RequireUUIDParams>
          </RequireAuth>
        }
      />
      <Route path="/candidate-opt-out/:orgId" element={<VerifyEmailPage />} />
      <Route path="/cancel-recording/:slug" element={<CancelRecordingPage />} />
      <Route
        path="/cancel-recording/meeting/:slug"
        element={<CancelRecordingByMeetingPage />}
      />
      <Route
        path="/candidate-opt-out/:orgId/:verificationCode"
        element={<CandidateEmailOptOutPage />}
      />
      <Route path="/launch/interview" element={<InterviewOptInPage />} />
      <Route path="/launch/interview/*" element={<InterviewOptInPage />} />
      <Route
        path="/external-share/:shareType/:shareId"
        element={
          <RequireUUIDParams paramNames={["shareId"]}>
            <ExternalSharePage />
          </RequireUUIDParams>
        }
      />
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route
        path="/register-info"
        element={
          <RequireAuth>
            <RegisterInfoPage />
          </RequireAuth>
        }
      />
      <Route
        path="/select-persona"
        element={
          <RequireAuth>
            <SelectPersonaPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sync-calendar"
        element={
          <RequireAuth>
            <SyncCalendarPage />
          </RequireAuth>
        }
      />
      <Route
        path="/register-phone"
        element={
          <RequireAuth>
            <VerifyPhonePage />
          </RequireAuth>
        }
      />
      <Route
        path="/greenhouse-auto-draft"
        element={
          <RequireAuth>
            <SelectScorecardAutoDraftPage />
          </RequireAuth>
        }
      />
      <Route
        path="/install-zoom-app"
        element={
          <RequireAuth>
            <InstallZoomApp />
          </RequireAuth>
        }
      />
      <Route
        path="/search"
        element={
          <RequireAuth>
            <SearchPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings/*"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/compliance-check/log"
        element={
          <RequireAuth>
            <DataRedactionLog />
          </RequireAuth>
        }
      />
      <Route
        path="/support"
        element={<RequireAuth>{<SupportPage />}</RequireAuth>}
      />
      <Route
        path="/not-found"
        element={
          <RequireAuth>
            <NotFound />
          </RequireAuth>
        }
      />
      <Route
        path="/upgrade"
        element={
          <RequireAuth>
            <UpgradePage
              headerText="Dramatically more efficient hiring"
              bodyText="BrightHire Interview streamlines your process with real-time interviewer guidance, AI-powered notes and highlights, actionable insights, and more."
              buttonText="Upgrade to Interview today"
              upgradeImageSrc="/static/images/interview-upgrade.png"
            />
          </RequireAuth>
        }
      />
      <Route path="/extension-installed" element={<ExtensionLandingPage />} />
      <Route
        path="/unauthorized"
        element={
          <RequireAuth>
            <NoAccessPage />
          </RequireAuth>
        }
      />
      <Route
        path="/forbidden"
        element={
          <RequireAuth>
            <Forbidden />
          </RequireAuth>
        }
      />
      <Route
        path="*"
        element={
          <RequireAuth>
            <NotFound />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

const UsersPageRedirect = (): JSX.Element => {
  const { pathname } = useLocation();

  return (
    <RequireAuth>
      <Navigate to={`/settings${pathname}`} replace />
    </RequireAuth>
  );
};

const InsightsPageRedirect = (): JSX.Element => {
  const { metric = "" } = useParams();
  const [searchParams] = useSearchParams();
  return (
    <RequireAuth>
      <Navigate
        replace
        to={`/insights/${metric}${
          searchParams ? `?${searchParams as unknown as string}` : ""
        }`}
      />
    </RequireAuth>
  );
};

const RequireAdvancedCustomTemplates: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const enabled = useAdvancedCustomTemplatesEnabled();
  if (!enabled) {
    return <NotFound />;
  }
  return <>{children}</>;
};
